import { useEffect, useMemo, useState } from 'react';

import { useScrollObserverStore } from '../stores/useScrollObserverStore';

import type {
  ScrollObserverEntryId,
  ScrollObserverRootId,
} from '../provider/ScrollObserverProvider';

type UseScrollObserverEntryProps = {
  rootId: ScrollObserverRootId;
};

const useLastIntersectingEntry = ({ rootId }: UseScrollObserverEntryProps) => {
  const entries = useScrollObserverStore(state => state.entries);

  const [localEntries, setLocalEntries] = useState<
    Array<{
      entryId: ScrollObserverEntryId;
      isLastIntersecting: boolean;
      lastIntersectingTime: number;
    }>
  >([]);

  useEffect(() => {
    const entriesInRoot = entries.filter(entry => entry.rootId === rootId);
    const lastIntersetingEntry = entriesInRoot
      .filter(entry => entry.isIntersecting)
      .sort((a, b) => b.time - a.time)?.[0];

    if (lastIntersetingEntry) {
      setLocalEntries(prevEntries => {
        const prevLastIntersetingEntry = prevEntries.find(entry => entry.isLastIntersecting);

        if (!prevLastIntersetingEntry) {
          // 기존 lastIntersetingEntry가 없다면, lastIntersetingEntry만 세팅, 이외 초기화
          return entriesInRoot.map(entry => ({
            entryId: entry.entryId,
            isLastIntersecting: lastIntersetingEntry.entryId === entry.entryId,
            lastIntersectingTime:
              lastIntersetingEntry.entryId === entry.entryId ? lastIntersetingEntry.time : 0,
          }));
        } else {
          // 기존 lastIntersetingEntry가 있다면,
          if (prevLastIntersetingEntry.lastIntersectingTime > lastIntersetingEntry.time) {
            // 기존 lastIntersetingEntry 보다 더 최신에 intersecting된 entry가 없다면 업데이트하지 않는다.
            return prevEntries;
          } else {
            // 기존 lastIntersetingEntry 보다 더 최신에 intersecting된 entry가 있다면, isLastIntersecting과 time을 업데이트한다.
            return prevEntries.map(entry => ({
              entryId: entry.entryId,
              isLastIntersecting: lastIntersetingEntry.entryId === entry.entryId,
              lastIntersectingTime: entry.lastIntersectingTime || 0,
            }));
          }
        }
      });
    }
  }, [entries, rootId]);

  const lastIntersetingEntry = useMemo(
    () => localEntries.find(entry => entry.isLastIntersecting),
    [localEntries],
  );

  return lastIntersetingEntry?.entryId;
};

export default useLastIntersectingEntry;
